/**
 * NUDIENT: Merförsäljningen "Bundle with", M-153. Plan:
 * nudient/PLAN-merforsaljning.md. Markup i blocks/nudient-bundle-with.liquid
 * och sections/nudient-bundle-with-render.liquid. Ersätter det gamla temats
 * assets/component-product-recommendation.js.
 *
 * nudient-bundle-with-box, en ruta per kollektion:
 * - Hämtar kollektionen med Section Rendering API och filtren, som idag:
 *   sorterat på namn, storleken eller kundens modell, och produktens färg.
 *   Modellen läses ur modellminnet, getModel, och utan eget val gäller
 *   standardmodellen, getDefaultModel, M-007. Värdet är postens model, som
 *   filtret jämför med passformen, compatible_models, M-153 punkt 3.
 * - En Cabin visar Check-in: med data-exclude-size väljs produktens egen
 *   storlek bort efter första hämtningen, och den första andra storleken i
 *   filtret hämtas, M-153. En hämtning till, bara på Cabin-sidor.
 * - Ett byte av storlek eller skaltyp hämtar om med det nya filtret. Ett byte
 *   av modell, i rutans väljare eller någon annanstans, hämtar om vid
 *   model:change. Rutans väljare sparar valet som alla väljare, med
 *   setModel i assets/nudient-model-picker.js.
 * - En färgprick byter produkt i rutan utan ny hämtning: bild, namn, pris
 *   och knappens variant ur prickens data. Färgnamnet står inte efter
 *   etiketten, M-153 tillägg 3, utan i prickens namn och namnruta.
 * - Knappen bär data-nudient-cart-add, och assets/nudient-cart.js lägger
 *   varianten i varukorgen med Shopifys vanliga anrop och Horizons händelse,
 *   så att Horizons låda öppnas.
 * - Rutan visas först när innehållet har kommit, och döljs om kollektionen
 *   inte ger något.
 * - Rabatten, M-153 tillägget 3 november 2026: har rutan data-discount, och
 *   ligger den visade produkten i data-discount-collection, står det
 *   ordinarie priset överstruket och det nedsatta bredvid. Det nedsatta är
 *   priset minus procenten av priset, avrundad till minsta enhet, som
 *   rabattens belopp i Shopify. Formaterat med Horizons formatMoney och
 *   butikens prisformat, utan ören när de är noll, som
 *   money_without_trailing_zeros och assets/nudient-bundle.js. Annars bara
 *   priset, som förut. Samma vid varje färgprick.
 *
 * Värden som inte är tokens:
 * - sort_by=title-ascending och filter.p.m.attribute.bag_size,
 *   compatible_models och colour_for_filtering: det gamla temats filter.
 * - nudient:swatches: händelsen som låter assets/nudient-product.js sätta
 *   färgprickarnas ton.
 * - 100: procent.
 */

import { findModel, getDefaultModel, getModel } from '@nudient/model-store';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Som money_without_trailing_zeros: hela belopp utan ören, M-057.
 * @param {number} minor - Beloppet i minsta enhet
 * @param {string} format - Butikens prisformat
 * @param {string} currency - Valutans kod
 */
function money(minor, format, currency) {
  const unit = convertMoneyToMinorUnits('1', currency) || 1;
  const formatted = formatMoney(minor, format, currency);
  if (unit === 1 || minor % unit !== 0) return formatted;
  const zeros = String(unit).length - 1;
  return formatted.replace(new RegExp(`[.,]0{${zeros}}(?=\\D*$)`), '');
}

const FILTER = 'filter.p.m.attribute.';

class NudientBundleWithBox extends HTMLElement {
  #controller = /** @type {AbortController | null} */ (null);
  #filters = new Map();

  connectedCallback() {
    this.content = this.querySelector('[data-nudient-bundle-with-content]');
    if (this.dataset.size) this.#filters.set(`${FILTER}bag_size`, this.dataset.size);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onSwatch);
    document.addEventListener('model:change', this.#onModel);
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onSwatch);
    document.removeEventListener('model:change', this.#onModel);
    this.#controller?.abort();
  }

  #url() {
    const url = new URL(this.dataset.url || '', window.location.origin);
    url.searchParams.set('sort_by', 'title-ascending');
    if (this.dataset.colour) url.searchParams.set(`${FILTER}colour_for_filtering`, this.dataset.colour);
    for (const [key, value] of this.#filters) url.searchParams.set(key, value);
    if (this.dataset.kind !== 'suitcase') {
      const model = findModel(getModel()) || getDefaultModel();
      if (model) url.searchParams.set(`${FILTER}compatible_models`, model.model || model.name);
    }
    url.searchParams.set('sections', this.dataset.section || '');
    return url;
  }

  async #load() {
    if (!this.content || !this.dataset.url) return;
    this.#controller?.abort();
    this.#controller = new AbortController();
    try {
      const response = await fetch(this.#url(), { signal: this.#controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const json = await response.json();
      const html = new DOMParser().parseFromString(json[this.dataset.section || ''] || '', 'text/html');
      const card = html.querySelector('[data-nudient-bundle-with-card]');
      const hasContent = Boolean(card?.querySelector('[data-nudient-bundle-with-add], nudient-model-picker'));
      this.content.replaceChildren(...(card && hasContent ? [document.importNode(card, true)] : []));
      if (this.#excludeOwnSize()) return;
      this.hidden = !hasContent;
      if (hasContent) {
        const price = this.content.querySelector('[data-nudient-bundle-with-price]');
        if (price instanceof HTMLElement) this.#price(price.dataset);
        this.content.dispatchEvent(new CustomEvent('nudient:swatches', { bubbles: true }));
      }
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      this.hidden = true;
    }
  }

  /** Väljer bort produktens egen storlek en gång, M-153. Sant om en ny hämtning startade. */
  #excludeOwnSize() {
    const exclude = this.dataset.excludeSize;
    const key = `${FILTER}bag_size`;
    if (!exclude || this.#filters.has(key)) return false;
    const select = this.content?.querySelector(`select[name="${key}"]`);
    if (!(select instanceof HTMLSelectElement) || select.value !== exclude) return false;
    const other = [...select.options].find((option) => option.value !== exclude);
    if (!other) return false;
    this.#filters.set(key, other.value);
    this.#load();
    return true;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.name.startsWith(FILTER)) return;
    this.#filters.set(select.name, select.value);
    this.#load();
  };

  /**
   * Det nedsatta priset, M-153 tillägget. Utan rabatt för produkten: bara
   * priset, som det står.
   * @param {DOMStringMap} data - Prisets eller prickens data, minor och collections
   */
  #price(data) {
    const price = this.content?.querySelector('[data-nudient-bundle-with-price]');
    const was = this.content?.querySelector('[data-nudient-bundle-with-was]');
    if (!(price instanceof HTMLElement) || !(was instanceof HTMLElement)) return;
    const percent = Number(this.dataset.discount || 0);
    const collections = (data.collections || '').split(',');
    const minor = Number(data.minor);
    const { moneyFormat = '', currency = '' } = this.dataset;
    const applies = percent > 0 && percent < 100 && Number.isFinite(minor) && moneyFormat && currency && collections.includes(this.dataset.discountCollection || '');
    price.classList.toggle('nudient-bundle-with__now--sale', Boolean(applies));
    if (!applies) {
      was.hidden = true;
      was.replaceChildren();
      return;
    }
    const sale = minor - Math.round((minor * percent) / 100);
    const hidden = (text) => {
      const span = document.createElement('span');
      span.className = 'visually-hidden';
      span.textContent = `${text}\u00a0`;
      return span;
    };
    was.replaceChildren(hidden(this.dataset.labelRegular || ''), money(minor, moneyFormat, currency));
    was.hidden = false;
    price.replaceChildren(hidden(this.dataset.labelSale || ''), money(sale, moneyFormat, currency));
  }

  #onModel = () => {
    if (this.dataset.kind !== 'suitcase') this.#load();
  };

  /** @param {MouseEvent} event */
  #onSwatch = (event) => {
    const swatch = event.target instanceof Element ? event.target.closest('button.nudient-colour-swatch') : null;
    if (!(swatch instanceof HTMLButtonElement) || !this.contains(swatch)) return;
    const card = swatch.closest('[data-nudient-bundle-with-card]');
    if (!card) return;
    const data = swatch.dataset;

    for (const other of card.querySelectorAll('button.nudient-colour-swatch')) {
      const chosen = other === swatch;
      other.setAttribute('aria-pressed', String(chosen));
      if (chosen) other.setAttribute('aria-current', 'true');
      else other.removeAttribute('aria-current');
    }

    const image = card.querySelector('[data-nudient-bundle-with-image]');
    if (image instanceof HTMLImageElement && data.image) {
      image.removeAttribute('srcset');
      image.src = data.image;
    }
    const title = card.querySelector('[data-nudient-bundle-with-title]');
    if (title && data.title) title.textContent = data.title;
    const price = card.querySelector('[data-nudient-bundle-with-price]');
    if (price && data.price) price.textContent = data.price;
    this.#price(data);

    const button = card.querySelector('[data-nudient-bundle-with-add]');
    if (button instanceof HTMLButtonElement && data.variant) {
      const available = data.available === 'true';
      button.setAttribute('data-nudient-cart-add', data.variant);
      button.disabled = !available;
      const label = button.querySelector('.nudient-button__label') || button;
      label.textContent = (available ? button.dataset.labelAdd : button.dataset.labelSoldOut) || '';
    }
  };
}

if (!customElements.get('nudient-bundle-with-box')) customElements.define('nudient-bundle-with-box', NudientBundleWithBox);
